import { useQuery } from 'react-query';
import ApiClient from 'lib/SanityClient/ApiClient';
import { ArticleType } from 'types';
import type { ArticleLink, ArticleType as IArticleType } from 'types';
import type { IDrugLink } from 'state/sanitizers/sanitizeDrugLink';

type Response = {
  query: string;
  articles: Array<ArticleLink | IDrugLink> | null;
  count: number;
};

type Options = {
  articleType?: IArticleType;
  hasLanguageChanged: boolean;
  isTaxonomyBrowsePage?: boolean;
  pageNumber: number;
  limit: number;
  language?: string;
  slug?: string;
};

class ArticleRequest {
  static create(options: Options): {
    key: string;
    fetcher: () => Promise<Response>;
  } {
    if (
      !options.articleType ||
      (options.pageNumber === 1 && !options.hasLanguageChanged)
    ) {
      return {
        key: '',
        fetcher: async () => ({
          query: '',
          articles: null,
          count: 0,
        }),
      };
    }
    if (options.isTaxonomyBrowsePage && options.articleType) {
      return ArticleRequest.taxonomyBrowsePage(options);
    } else {
      switch (options.articleType) {
        case ArticleType.Dx:
          return ArticleRequest.dxArticle(options);
        case ArticleType.Sx:
          return ArticleRequest.sxArticle(options);
        case ArticleType.Cost:
          return ArticleRequest.costArticle(options);
        case ArticleType.Other:
          return ArticleRequest.otherArticle(options);
        default:
          console.warn(`Unsupported article type: ${options.articleType}`);
          return {
            key: '',
            fetcher: async () => ({
              query: '',
              articles: null,
              count: 0,
            }),
          };
      }
    }
  }

  private static taxonomyBrowsePage(options: Options) {
    const key = `browsePage-${options.slug}-articles-${options.pageNumber}-${options.limit}-${options.language}`;
    return {
      key,
      fetcher: async () => {
        const response = await ApiClient.fetchBrowsePage({
          articleType: options.articleType as ArticleType,
          slug: options.slug || '',
          pageNumber: options.pageNumber,
          language: options.language,
          limit: options.limit,
        });
        return {
          query: key,
          articles: response.browsePage?.articles || null,
          count: response.count,
        };
      },
    };
  }
  private static costArticle(options: Options) {
    const key = `cost-articles-${options.pageNumber}-${options.limit}-${options.language}`;
    return {
      key,
      fetcher: async () => {
        const response = await ApiClient.fetchCostArticles({
          pageNumber: options.pageNumber,
          language: options.language,
          limit: options.limit,
        });
        return {
          query: key,
          articles: response.articles,
          count: response.count,
        };
      },
    };
  }
  private static dxArticle(options: Options) {
    const key = `dx-articles-${options.pageNumber}-${options.limit}-${options.language}`;
    return {
      key,
      fetcher: async () => {
        const response = await ApiClient.fetchDxArticles({
          pageNumber: options.pageNumber,
          language: options.language,
          limit: options.limit,
        });
        return {
          query: key,
          articles: response.articles,
          count: response.count,
        };
      },
    };
  }
  private static sxArticle(options: Options) {
    const key = `sx-articles-${options.pageNumber}-${options.limit}-${options.language}`;
    return {
      key,
      fetcher: async () => {
        const response = await ApiClient.fetchSxArticles({
          pageNumber: options.pageNumber,
          language: options.language,
          limit: options.limit,
        });
        return {
          query: key,
          articles: response.articles,
          count: response.count,
        };
      },
    };
  }
  private static otherArticle(options: Options) {
    const key = `other-articles-${options.pageNumber}-${options.limit}-${options.language}`;
    return {
      key,
      fetcher: async () => {
        const response = await ApiClient.fetchOtherArticles({
          pageNumber: options.pageNumber,
          language: options.language,
          limit: options.limit,
        });
        return {
          query: key,
          articles: response.articles,
          count: response.count,
        };
      },
    };
  }
}

function useArticles(options: Options) {
  const request = ArticleRequest.create(options);
  return useQuery<Response>({
    queryKey: [request.key],
    queryFn: request.fetcher,
    staleTime: 1000 * 60 * 60, // 1 hour
    cacheTime: 1000 * 60 * 60 * 24, // 1 day
  });
}

export default useArticles;
